import { useRangeCalendarState as originalUseRangeCalendarState } from 'react-stately/useRangeCalendarState';
import {
  RangeCalendarStateOptions as OriginalRangeCalendarStateOptions,
  RangeCalendarState as OriginalRangeCalendarState,
} from 'react-stately/useRangeCalendarState';
import { DateValue } from '@internationalized/date';
import { useRef } from 'react';
import { ValueBasePropsKeys } from '../../utils/types';

type OriginalOnChange<T extends DateValue> = NonNullable<
  OriginalRangeCalendarStateOptions<T>['onChange']
>;

export type RangeCalendarStateOptions<
  T extends DateValue,
  P extends string
> = Pick<
  OriginalRangeCalendarStateOptions<T>,
  | ValueBasePropsKeys
  | 'locale'
  | 'createCalendar'
  | 'defaultFocusedValue'
  | 'allowsNonContiguousRanges'
  | 'visibleDuration'
> & {
  onChange?: (
    value: Parameters<OriginalOnChange<T>>[0],
    predefinedRange?: P
  ) => ReturnType<OriginalOnChange<T>>;
};

type OriginalSetValue<T extends DateValue> =
  OriginalRangeCalendarState<T>['setValue'];

export type RangeCalendarState<
  P extends string,
  T extends DateValue = DateValue
> = Omit<OriginalRangeCalendarState<T>, 'setValue'> & {
  setValue: (
    value: Parameters<OriginalSetValue<T>>[0],
    predefinedRange?: P
  ) => ReturnType<OriginalSetValue<T>>;
};

/**
 * A wrapper to allow for the onChange callback to have extra info
 **/
export const useRangeCalendarState = <
  T extends DateValue = DateValue,
  P extends string = string
>({
  onChange,
  ...options
}: RangeCalendarStateOptions<T, P>): RangeCalendarState<P, T> => {
  const lastPredefinedRange = useRef<P | undefined>(undefined);

  const state = originalUseRangeCalendarState<T>({
    ...options,
    onChange: (value) => {
      onChange?.(value, lastPredefinedRange.current);
      lastPredefinedRange.current = undefined;
    },
  });

  return {
    ...state,
    setValue: (value, predefinedRange) => {
      lastPredefinedRange.current = predefinedRange;
      state.setValue(value);
    },
  };
};
